// components/auth/StoreProfileGate.tsx
//
// Modal that prompts a logged-in store owner to fill in their
// first + last name when their user record doesn't carry them
// yet. Catches the population of pre-existing store accounts
// claimed before the invite flow started collecting these
// fields.
//
// Mounted at the app root inside AuthProvider so it sits on top
// of any route the store user lands on. The dialog is non-
// dismissible (no close button, no backdrop-click close); the
// only way past it is to fill the fields and save.

import { ReactElement, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Box,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { ref, update } from 'firebase/database';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';

export function StoreProfileGate(): ReactElement | null {
  const { user } = useAuth();
  const { error: showError } = useNotification();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);

  // The gate is shown only for store accounts whose user record
  // is missing first OR last name. The `accountType` check is
  // strict equality - we don't want to nag admins / store-
  // manager proxies / staff.
  const needsProfile =
    user?.accountType === 'store' &&
    (!user.firstName || !user.lastName);

  // Pre-fill the inputs if one of the two is already populated
  // (e.g. an older store user that has firstName but not lastName).
  useEffect(() => {
    if (!needsProfile) return;
    setFirstName(user?.firstName || '');
    setLastName(typeof user?.lastName === 'string' ? user.lastName : '');
    // We intentionally only seed on the show-gate transition;
    // the user editing the inputs shouldn't be clobbered by a
    // re-render of the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsProfile]);

  if (!needsProfile || !user?.uid) return null;

  const trimmedFirst = firstName.trim();
  const trimmedLast = lastName.trim();
  const canSave = trimmedFirst.length > 0 && trimmedLast.length > 0 && !saving;

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    try {
      await update(ref(db, `users/${user.uid}`), {
        firstName: trimmedFirst,
        lastName: trimmedLast,
      });
      // No need to close manually - the AuthContext live
      // subscription will push the updated record back into
      // `user`, needsProfile will flip to false, and this
      // component unmounts.
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save your name');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      disableEscapeKeyDown
      onClose={(_e, reason) => {
        // Block backdrop click - the gate is mandatory.
        if (reason === 'backdropClick') return;
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon sx={{ color: '#00FFD4' }} />
        Tell us your name
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          We collect first and last name for store accounts so
          dashboards and operational contacts have a real person
          attached. The store name stays as your public-facing
          label.
        </DialogContentText>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            autoComplete="given-name"
            autoFocus
            fullWidth
          />
          <TextField
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            autoComplete="family-name"
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSave} disabled={!canSave} variant="contained" color="primary">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default StoreProfileGate;
